<template>
  <IdentityShell top-link="none" aside-mobile="last">
    <div class="directory-entry">
      <p class="kicker">
        {{
          $t('auth.identity.directoryEntry.newToCommunal', 'New to Communal')
        }}
      </p>

      <h1 class="section-headline directory-entry__title">
        {{ $t('auth.identity.directoryEntry.startYour', 'Start your') }}
        <span class="highlight">{{
          $t('auth.identity.directoryEntry.freeTrial', 'free trial')
        }}</span
        >{{ $t('auth.identity.directoryEntry.freeTrialTrail', '.') }}<br />{{
          $t(
            'auth.identity.directoryEntry.seeItLive',
            'See it live, no commitment.'
          )
        }}
      </h1>

      <div class="directory-entry__actions">
        <RouterLink
          to="/signup"
          class="btn-create btn-create--lg directory-entry__create"
        >
          {{
            $t(
              'auth.identity.directoryEntry.startFreeTrial',
              'Start free trial'
            )
          }}
          <span class="directory-entry__arrow" aria-hidden="true">→</span>
        </RouterLink>
      </div>

      <ul
        class="directory-entry__proof"
        :aria-label="$t('auth.identity.directoryEntry.trustedBy', 'Trusted by')"
      >
        <li>
          {{
            $t(
              'auth.identity.directoryEntry.trustedByCount',
              'Trusted by 500+ organizations'
            )
          }}
        </li>
        <li
          class="directory-entry__proof-rating"
          :aria-label="
            $t(
              'auth.identity.directoryEntry.ratedOnCapterra',
              'Rated 4.7 on Capterra'
            )
          "
        >
          <IconStar solid class="directory-entry__proof-star" />
          {{
            $t('auth.identity.directoryEntry.capterraRating', '4.7 on Capterra')
          }}
        </li>
      </ul>

      <IdentityTestimonial
        quote="Revenue from membership sales and renewals increased, and we save significant staff time and paperwork."
        initials="RD"
        author="Renee D."
        org="North Glenmore Park Community Association"
      >
        <template #creds>
          <li class="testimonial__cred">
            {{
              $t('auth.identity.directoryEntry.membersCount', '800+ members')
            }}
          </li>
        </template>
      </IdentityTestimonial>
    </div>

    <template #welcome>
      <div class="login-aside">
        <p class="kicker">
          {{
            $t(
              'auth.identity.directoryEntry.alreadyUseCommunal',
              'Already use Communal'
            )
          }}
        </p>

        <header class="login-aside__head">
          <h2 class="login-aside__title">
            {{
              $t(
                'auth.identity.directoryEntry.signInToYourOrganization',
                'Sign in to your organization.'
              )
            }}
          </h2>
          <p class="login-aside__sub">
            {{
              $t(
                'auth.identity.directoryEntry.signInCodeSub',
                "Enter your email and we'll send a secure 6-digit sign-in code."
              )
            }}
          </p>
        </header>

        <AlertInfo v-if="notice" show class="mb-3">
          {{ notice }}
        </AlertInfo>

        <AlertDanger v-if="firstError" show class="mb-3">
          {{ firstError }}
        </AlertDanger>

        <FormBase
          class="auth-form login-aside__form"
          @submit.prevent="onSubmit"
        >
          <FormGroupBase
            class="mb-3"
            :label="$t('auth.identity.directoryEntry.emailLabel', 'Email')"
            :state="fieldState('email')"
            :invalid-feedback="fieldError('email')"
          >
            <InputEmail
              v-model="email"
              autocomplete="email"
              :placeholder="
                $t(
                  'auth.identity.directoryEntry.emailPlaceholder',
                  'you{\'@\'}example.com'
                )
              "
              :state="fieldState('email')"
            />
          </FormGroupBase>

          <button
            type="submit"
            class="btn-communal btn-communal--block login-aside__submit"
            :disabled="submitting"
          >
            <span v-if="!submitting">
              {{
                $t(
                  'auth.identity.directoryEntry.sendSignInCode',
                  'Send sign-in code'
                )
              }}
              <span class="btn-communal__arrow" aria-hidden="true">→</span>
            </span>
            <span v-else>
              {{
                $t('auth.identity.directoryEntry.sendingCode', 'Sending code…')
              }}
            </span>
          </button>
        </FormBase>

        <template v-if="hasSocialProviders">
          <div class="auth-divider login-aside__divider" aria-hidden="true">
            <span class="auth-divider__label">
              {{ $t('auth.identity.directoryEntry.or', 'or') }}
            </span>
          </div>

          <AuthSocialButtons intent="login" class="login-aside__social" />
        </template>
      </div>
    </template>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import { AuthSocialButtons } from '@/components/feature/Auth';
import {
  IdentityShell,
  IdentityTestimonial,
} from '@/components/feature/Identity';
import { AlertDanger, AlertInfo } from '@/components/ui/Alert';
import { InputEmail } from '@/components/ui/Form';
import { IconStar } from '@/components/ui/Icon';
import { useIdentityBoot } from '@/composables/useIdentityBoot';
import { useIdentityForm } from '@/composables/useIdentityForm';

const email = ref('');
const hasSocialProviders = computed(() =>
  Boolean(window.socialProviders?.google)
);

// One-shot notice surfaced by the server (e.g. the tenant signup handoff
// redirected here because the link expired). Cleared when the page mounts
// via useIdentityBoot.
const boot = useIdentityBoot();
const notice = computed(() => {
  const value = boot.data?.notice;
  return typeof value === 'string' && value !== '' ? value : null;
});

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/login' });

async function onSubmit() {
  await submit({ email: email.value });
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>, raw
// submit <button>, .section-headline, .btn-communal, and .btn-create are
// deliberate marketing-surface classes, not in-product wrappers. See
// IdentityShell.vue for the full contract.
//
// On /login the main card carries the primary path (Start free trial) and the
// welcome aside carries the secondary path (existing-user sign-in). The
// shell's 7fr/5fr grid plus the card chrome on the left already biases the
// signup CTA; the aside intentionally has no card so the form reads as the
// quieter, returning-visitor option.

.directory-entry__title {
  margin-bottom: 1.5rem;
}

.directory-entry__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.9rem;
  margin-bottom: 1.75rem;
}

.directory-entry__create {
  align-self: flex-start;
}

.directory-entry__arrow {
  display: inline-block;
  transition: transform 200ms var(--ease-decelerate);
}

.btn-create:hover .directory-entry__arrow,
.btn-create:focus-visible .directory-entry__arrow {
  transform: translateX(3px);
}

.directory-entry__proof {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1.25rem;
  margin: 0 0 1.75rem;
  padding: 0 0 1.5rem;
  border-bottom: 1px solid var(--rule);
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--ink-700);

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
}

.directory-entry__proof-rating {
  color: var(--ink-800);
}

.directory-entry__proof-star {
  width: 0.95rem;
  height: 0.95rem;
  color: var(--rating-star);
}

// Login aside: secondary, no card chrome. Smaller heading, tighter form so
// the eye lands on the left-card primary CTA first.
.login-aside {
  display: flex;
  flex-direction: column;
  width: 100%;

  > .kicker {
    margin: 0 0 0.75rem;
  }
}

.login-aside__head {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0 0 1.25rem;
}

.login-aside__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--ink-900);
}

.login-aside__sub {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--ink-600);
}

.login-aside__form {
  width: 100%;
}

// Aside CTA runs slightly smaller than the shared --block sizing so the
// left-card primary CTA keeps the visual lead.
.login-aside__submit {
  padding: 0.7rem 1.2rem;
  font-size: 0.9375rem;
}

.login-aside__divider {
  margin: 1.1rem 0 0.9rem;
}

// The aside has no card behind it, so the provider button is the one element
// here with nothing to sit on. It keeps the shared marketing-surface treatment
// (edge + resting shadow) rather than the quieter variant it used to carry —
// muting it further left it invisible against the canvas.
.login-aside__social {
  margin-bottom: 0;
}
</style>
